import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, input } from '@angular/core';
import { FormArray, FormGroup, FormsModule } from '@angular/forms';
import { TabsInnerWrapComponent } from '../../components/tabs-inner-wrap/tabs-inner-wrap.component';
import { DetailsSectionComponent } from '../../components/details-container/details-section.component';
import {
  KebabMenuItem,
  QaiButtonComponent,
  QaiFloatingDirective,
  QaiLoadingComponent,
  QaiMultiSelectComponent,
  QaiMultiSelectOption,
  QaiTableComponent,
  QaiTooltipComponent,
  SortDirection,
  TableRow,
} from '@qai/ui';
import { ActivatedRoute } from '@angular/router';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { IAssignments } from 'src/app/interfaces/self-serve/responses/iassignments';
import { AuthorReactiveFormService } from '../../services/author-reactive-form.service';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import {
  catchError,
  firstValueFrom,
  forkJoin,
  Observable,
  of,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { formatLongMonthDate } from 'src/app/utils/date-parser.util';
import { hasNoMessagingThemes, hasIncompleteMessagingTheme } from 'src/app/utils/messaging-facts.util';
import { hasMissingSkillsRubric } from 'src/app/utils/skills-rubric.util';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LocaleService } from 'src/app/services/locale.service';
import { CallRefinementPollingService } from 'src/app/services/self-serve/call-refinement-polling.service';
import { ScoringRefinementService } from 'src/app/services/self-serve/scoring-refinement.service';

// NOTE: These strings are used as both display headers and data-row keys by QaiTable — must stay as English literals.
export const TESTING_TABLE_HEADERS = [
  'Name',
  'Number of attempts',
  'Date of last attempt',
];

@Component({
  selector: 'tab-testing-details',
  standalone: true,
  templateUrl: './testing-details.component.html',
  styleUrls: ['./testing-details.component.scss'],
  imports: [
    FormsModule,
    CommonModule,
    DetailsSectionComponent,
    TabsInnerWrapComponent,
    QaiTableComponent,
    QaiButtonComponent,
    QaiMultiSelectComponent,
    QaiLoadingComponent,
    QaiTooltipComponent,
    QaiFloatingDirective,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class TestingDetailsComponent implements OnInit, OnDestroy {
  isSelfAuthoring = input(false);

  @ViewChild('multiselect') searchInput!: QaiMultiSelectComponent;

  private readonly destroy$ = new Subject<void>();

  tableData: TableRow[] = [];
  readonly headers = TESTING_TABLE_HEADERS;
  initialSortColumn: string = TESTING_TABLE_HEADERS[0];
  initialSortDirection: SortDirection = 'asc';
  public searchOptions: QaiMultiSelectOption[] = [];
  clearOption = { enable: true, label: '' };
  visibleOptionsSelectionMode = {
    enable: true,
    selectAllOption: { value: 'selectAll', label: '' },
    deselectAllOption: { value: 'deselectAll', label: '' },
  };

  isLoading = false;
  sendAssignmentNetworkCallInprogress = false;

  accountUuid: string | null = null;

  form!: FormGroup;
  baseForm!: FormGroup;
  overallForm!: FormGroup;
  callUuid: string = '';
  readOnly: boolean = false;
  description: string = '';

  testersList: string[] = [];
  defaultSelection: string[] = [];
  assignments: IAssignments[] = [];
  networkRequestInProgress: boolean = false;
  v2HasNoFacts: boolean = false;
  isMissingMessagingThemes: boolean = false;
  hasEmptyFacts: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private authorService: AuthorService,
    private cdRef: ChangeDetectorRef,
    private authorReactiveFormService: AuthorReactiveFormService,
    private translocoService: TranslocoService,
    private callRefinementPollingService: CallRefinementPollingService,
    private scoringRefinementService: ScoringRefinementService,
    private localeService: LocaleService
  ) { }

  async ngOnInit() {
    this.description = this.translocoService.translate('simulationBuilder.TESTING_DESCRIPTION');
    this.clearOption = { enable: true, label: this.translocoService.translate('simulationBuilder.TESTING_CLEAR_USERS') };
    this.visibleOptionsSelectionMode = {
      enable: true,
      selectAllOption: { value: 'selectAll', label: this.translocoService.translate('simulationBuilder.SELECT_ALL') },
      deselectAllOption: { value: 'deselectAll', label: this.translocoService.translate('simulationBuilder.DESELECT_ALL') },
    };

    this.callUuid = this.route.snapshot.paramMap.get('uuid')!;
    this.readOnly = this.authorReactiveFormService.isReadOnly(this.callUuid);

    await this.loadForm();

    this.isLoading = true;
    forkJoin([this.getUsers(), this.getAssignments()]).subscribe(
      () => {
        this.isLoading = false;
        this.cdRef.detectChanges();
      }
    );
  }

  /** True while a refinement is scheduled (ScoringRefinementService debounce) or still running (polling). */
  get isRefinementInProgress(): boolean {
    return (
      this.callRefinementPollingService.isRefining(this.callUuid) ||
      this.scoringRefinementService.isPending(this.callUuid)
    );
  }

  // Getter, not a cached flag: the Skills section loads the saved rubric with emitEvent: false,
  // so valueChanges never fires to refresh a cached value.
  get isMissingSkillsRubric(): boolean {
    return this.isSelfAuthoring() && !!this.baseForm && hasMissingSkillsRubric(this.baseForm);
  }

  get readyForPublish(): boolean {
    return (
      (this.baseForm?.valid ?? false) &&
      !this.v2HasNoFacts &&
      !this.isMissingMessagingThemes &&
      !this.hasEmptyFacts &&
      !this.isMissingSkillsRubric
    );
  }

  private async loadForm(): Promise<void> {
    this.baseForm = await firstValueFrom(
      this.authorReactiveFormService.getForm(this.callUuid)
    );

    this.form = this.baseForm.get(
      SimulationConstants.CALL_PERSONA_PATH
    ) as FormGroup;

    this.baseForm.statusChanges?.subscribe(() => {
      this.readOnly = this.authorReactiveFormService.isReadOnly(this.callUuid);
    });

    this.updateV2NoFactsState();
    this.baseForm.valueChanges?.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateV2NoFactsState());
  }

  private updateV2NoFactsState(): void {
    const promptType = this.baseForm?.get(`${SimulationConstants.MESSAGING_PROMPT_PATH}.promptType`)?.value;
    const factSections = this.baseForm?.get(
      `${SimulationConstants.SCORING_SECTION}.${SimulationConstants.MESSAGING_THEME_SECTION}.${SimulationConstants.FACT_SECTIONS_SECTION}`
    ) as FormArray | null;
    const previous = this.v2HasNoFacts;
    this.v2HasNoFacts =
      promptType === SimulationConstants.MESSAGING_PROMPT_TYPE_V2 &&
      (!factSections || factSections.length === 0);
    this.isMissingMessagingThemes = this.isSelfAuthoring() && !!this.baseForm && hasNoMessagingThemes(this.baseForm);
    this.hasEmptyFacts = this.isSelfAuthoring() && !!this.baseForm && hasIncompleteMessagingTheme(this.baseForm);

    if (!previous && this.v2HasNoFacts) {
      // Backend has reverted the sim from TESTING → DRAFT and cleared test assignments
      // (all facts were removed). Refresh the table so cleared assignments reflect in the UI.
      this.getAssignments().subscribe();
    }
  }

  getUsers(): Observable<IAssignments[]> {
    const selectedAccountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    const currentAccountUuid = localStorage.getItem('current_account_uuid');
    const accountUuid = selectedAccountUuid || currentAccountUuid || '';
    return this.authorService.getUsersForTesting(accountUuid).pipe(
      tap((res) => {
        this.searchOptions = this.transformSearchDataToOptions(res);
      }),
      catchError((err) => {
        console.error(err);
        return of([]);
      })
    );
  }

  getAssignments(): Observable<IAssignments[]> {
    const moduleUuid = this.baseForm?.get(SimulationConstants.MODULE_UUID_PATH)?.value || '';
    return this.authorService.getAttempts(this.callUuid, moduleUuid).pipe(
      tap((res: IAssignments[]) => {
        this.assignments = res;
        this.tableData = this.transformTestingDataToTableRows(res);
      }),
      catchError((err) => {
        console.error(err);
        return of([]);
      })
    );
  }

  filterOutExistingAssignments() {
    const existingUuids = new Set(
      this.assignments.map((item) => item.userUuid)
    );

    this.testersList = this.testersList.filter(
      (uuid) => !existingUuids.has(uuid)
    );
  }

  handleSendAssignmentClick(event: Event) {
    if (this.isRefinementInProgress) return;
    this.networkRequestInProgress = true;
    this.sendTestAssignments();
  }

  sendTestAssignments() {
    this.filterOutExistingAssignments();
    if (this.testersList.length === 0) {
      this.defaultSelection = [];
      return;
    }
    const moduleUuid = this.baseForm?.get(SimulationConstants.MODULE_UUID_PATH)?.value || '';
    this.authorService
      .sendTestAssignment(this.callUuid, this.testersList, moduleUuid)
      .subscribe({
        next: () => {
          this.authorReactiveFormService.setSimulationStatus(this.callUuid, 'TESTING');
          this.defaultSelection = [];
          this.testersList = [];
          this.searchInput.clearSelections();
          this.getAssignments().subscribe();
          this.networkRequestInProgress = false;
        },
        error: (err) => {
          console.error(err);
          this.networkRequestInProgress = false;
        },
      });
  }

  transformSearchDataToOptions(apiData: any[]): QaiMultiSelectOption[] {
    return apiData.map((item) => ({
      value: item.uuid,
      label: `${item.firstName} ${item.lastName}`,
      description: item.email,
    }));
  }

  searchValueChange(event: string[]) {
    this.testersList = event;
  }

  searchSelectionChange(event: QaiMultiSelectOption[]) {
    console.log('searchSelectionChange', event);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  createTestingTableRow(
    id: string | number,
    name: string,
    numberOfAttempts: number,
    dateOfLastAttempt: string,
    options: KebabMenuItem[]
  ): TableRow {
    const row = {
      id,
      [TESTING_TABLE_HEADERS[0]]: { type: 'text', value: name },
      [TESTING_TABLE_HEADERS[1]]: { type: 'text', value: numberOfAttempts },
      [TESTING_TABLE_HEADERS[2]]: { type: 'text', value: dateOfLastAttempt },
    } as const;
    return { ...row, options } as unknown as TableRow;
  }

  onTableRowAction(event: {
    action: KebabMenuItem;
    rowId: string | number;
    rowData: TableRow;
    event: Event;
  }) {
    if (!event.action.text) return;

    switch (event.action.text.toLowerCase()) {
      case this.translocoService.translate('simulationBuilder.TESTING_REMOVE_ASSIGNEE').toLowerCase():
        this.removeAssignment(event.rowData.id as string);
        break;

      default:
        break;
    }
  }

  removeAssignment(userUuid: string): void {
    const moduleUuid = this.baseForm?.get(SimulationConstants.MODULE_UUID_PATH)?.value || '';
    this.authorService.removeAssignment(this.callUuid, userUuid, moduleUuid).subscribe({
      next: () => {
        console.log(`Assignment removed for user: ${userUuid}`);
        this.getUsers();
        this.getAssignments().subscribe();
      },
      error: (err: any) => {
        console.error('Failed to remove assignee', err);
      },
    });
  }

  transformTestingDataToTableRows(apiData: IAssignments[]): TableRow[] {
    return apiData.map((item) =>
      this.createTestingTableRow(
        item.userUuid,
        item.userName,
        item.numberOfAttempts,
        formatLongMonthDate(item.lastAttemptDate, this.localeService.getCurrentLocaleId()),
        [
          {
            type: 'click',
            text: this.translocoService.translate('simulationBuilder.TESTING_REMOVE_ASSIGNEE'),
          },
        ]
      )
    );
  }
}
